import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { ImageWithLink } from '../image-with-link.component';
import { TranslateService } from '@ngx-translate/core';
import { OtpInputComponent } from './otp-input.component';

@Component({
  selector: 'pt-two-factor-authentication-generate',
  template: `
    <div class="modal-header">
      <h4 class="modal-title">
        {{ 'TWO_FACTOR_AUTHENTICATION.GENERATE.TITLE' | translate }}
      </h4>
    </div>
    <div class="modal-body">
      <div class="generate-container">
        <div class="generate-container--left">
          <pt-image-with-text
            class="w-100"
            [image]="'assets/images/otp/download.png'"
            [texts]="[downloadMessage]"
          >
          </pt-image-with-text>

          <div class="download-link">
            <pt-image-with-link
              class="w-100"
              *ngFor="let link of links"
              [image]="link.image"
              [text]="link.text"
              [links]="link.links"
            >
            </pt-image-with-link>
          </div>

          <pt-image-with-text
            class="w-100"
            [image]="'assets/images/otp/qr-code.png'"
            [texts]="[scanQrcode, warnMessage]"
          >
          </pt-image-with-text>
        </div>
        <div class="generate-container--right">
          <div class="qr-code-container">
            <div *ngIf="!qrCodeLink">
              {{
                'TWO_FACTOR_AUTHENTICATION.GENERATE.MISSING_QRCODE' | translate
              }}
            </div>
            <pt-otp-qrcode [currentLink]="qrCodeLink"></pt-otp-qrcode>
          </div>
        </div>
      </div>

      <!-- Componente OTP riutilizzabile -->
      <div class="otp-section">
        <pt-otp-input
          #otpInputComponent
          [length]="6"
          [label]="'TWO_FACTOR_AUTHENTICATION.GENERATE.ENTER_CODE' | translate"
          [placeholder]="'000000'"
          [errorMessage]="'TWO_FACTOR_AUTHENTICATION.GENERATE.INVALID_CODE' | translate"
          [showCharCount]="false"
          (valueChange)="onOtpValueChange($event)"
          (isValidChange)="onOtpValidityChange($event)"
        ></pt-otp-input>
      </div>

      <p class="complete-message" [innerHTML]="completeMessage | async"></p>
    </div>
    <div class="modal-footer">
      <button
        type="button"
        class="btn btn-primary btn-sm"
        [disabled]="!qrCodeLink || !isOtpValid"
        (click)="onConfirmClicked(); $event.stopPropagation()"
      >
        {{ 'BUTTON.CONFIRM' | translate }}
      </button>
      <button
        ngbAutofocus
        type="button"
        class="btn btn-outline-secondary btn-sm"
        (click)="cancel(); $event.stopPropagation()"
      >
        {{ 'BUTTON.CANCEL' | translate }}
      </button>
    </div>
  `,
  styles: [
    `
      .download-link {
        margin-left: 50px;
        width: 300px;
      }

      .generate-container {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .generate-container--left {
        display: inline-flex;
        width: calc(100% - 210px);
        flex-direction: column;
      }

      .generate-container--right {
        display: inline-flex;
        width: 200px;
        align-items: center;
        justify-content: center;
        height: 200px;
      }

      .qr-code-container {
        border: 1px dashed #7d7d7d;
        width: 80%;
        height: 80%;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .complete-message {
        margin: 20px 0 0 0;
      }

      /* ──── OTP Section ──── */
      .otp-section {
        margin: 24px 0;
        padding: 28px;
        border-radius: 12px;
        background: #f4f7fb;
        border: 1px solid #e3e9f1;
      }

      /* ──── Responsive ──── */
      @media (max-width: 560px) {
        .otp-section {
          padding: 20px;
          margin: 18px 0;
        }

        .download-link {
          margin-left: 20px;
          width: 100%;
        }

        .generate-container {
          flex-direction: column;
          align-items: flex-start;
        }

        .generate-container--left {
          width: 100%;
        }

        .generate-container--right {
          width: 100%;
          margin-top: 20px;
        }
      }
    `,
  ],
})
export class TwoFactorAuthenticationGenerateComponent {
  @ViewChild('otpInputComponent') otpInputComponent: OtpInputComponent;

  @Input() qrCodeLink: string;
  @Input() forceConfiguration: boolean;

  @Output() confirmClicked = new EventEmitter<{
    qrCodeLink: string;
    otpCode: string;
  }>();
  @Output() cancelClicked = new EventEmitter<boolean>();
  @Output() logout = new EventEmitter<boolean>();
  @Output() otpCodeChanged = new EventEmitter<string>();

  isOtpValid: boolean = false;
  private otpCode: string = '';

  downloadMessage = this.translateService.stream(
    'TWO_FACTOR_AUTHENTICATION.GENERATE.DOWNLOAD_MESSAGE'
  );
  scanQrcode = this.translateService.stream(
    'TWO_FACTOR_AUTHENTICATION.GENERATE.SCAN_QRCODE'
  );
  warnMessage = this.translateService.stream(
    'TWO_FACTOR_AUTHENTICATION.GENERATE.WARN_QRCODE'
  );
  completeMessage = this.translateService.stream(
    'TWO_FACTOR_AUTHENTICATION.GENERATE.COMPLETE_MESSAGE'
  );

  links = [
    {
      image: 'assets/images/otp/google-authenticator.png',
      text: 'Google Authenticator',
      links: [
        {
          image: 'assets/images/otp/android.png',
          link: 'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2',
          tooltip: this.translateService.stream(
            'TWO_FACTOR_AUTHENTICATION.GENERATE.GOOGLE_AUTHENTICATOR'
          ),
        },
        {
          image: 'assets/images/otp/apple.png',
          link: 'https://apps.apple.com/it/app/google-authenticator/id388497605',
          tooltip: this.translateService.stream(
            'TWO_FACTOR_AUTHENTICATION.GENERATE.GOOGLE_AUTHENTICATOR'
          ),
        },
      ] as ImageWithLink[],
    },
    {
      image: 'assets/images/otp/microsoft-authenticator.png',
      text: 'Microsoft Authenticator',
      links: [
        {
          image: 'assets/images/otp/android.png',
          link: 'https://play.google.com/store/apps/details?id=com.azure.authenticator',
          tooltip: this.translateService.stream(
            'TWO_FACTOR_AUTHENTICATION.GENERATE.MICROSOFT_AUTHENTICATOR'
          ),
        },
        {
          image: 'assets/images/otp/apple.png',
          link: 'https://apps.apple.com/it/app/microsoft-authenticator/id983156458',
          tooltip: this.translateService.stream(
            'TWO_FACTOR_AUTHENTICATION.GENERATE.MICROSOFT_AUTHENTICATOR'
          ),
        },
      ] as ImageWithLink[],
    },
  ];

  constructor(private translateService: TranslateService) {}

  /**
   * Handler per il cambio del valore OTP dal componente OtpInput
   */
  onOtpValueChange(value: string): void {
    this.otpCode = value;
    this.otpCodeChanged.emit(value);
  }

  /**
   * Handler per il cambio della validità OTP dal componente OtpInput
   */
  onOtpValidityChange(isValid: boolean): void {
    this.isOtpValid = isValid;
  }

  /**
   * Gestisce il click del bottone Confirm
   */
  onConfirmClicked(): void {
    if (this.isOtpValid && this.qrCodeLink) {
      this.confirmClicked.emit({
        qrCodeLink: this.qrCodeLink,
        otpCode: this.otpCode,
      });
    }
  }

  /**
   * Gestisce il cancel
   */
  protected cancel(): void {
    if (this.forceConfiguration) {
      this.logout.emit(true);
    } else {
      this.cancelClicked.emit(true);
    }
  }
}
